import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  categorySlug: string;
  coverImage: string;
  author: string;
  publishedAt: string;
  readTime: number;
  tags: string[];
  views: number;
  isFeatured?: boolean;
}

function getCategoryName(slug: string): string {
  return categories.find((c) => c.slug === slug)?.name || slug;
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  categorySlug,
  coverImage,
  author,
  publishedAt,
  readTime,
  tags,
  views,
  isFeatured,
}: ArticleCardProps) {
  const categoryName = getCategoryName(categorySlug);

  return (
    <article className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300">
      <Link to={`/article/${slug}`} className="block">
        <div className="relative overflow-hidden aspect-[16/9]">
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {isFeatured && (
            <span className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs px-2 py-1 rounded-md font-medium">
              热门推荐
            </span>
          )}
          <div className="absolute top-3 right-3 bg-background-950/60 backdrop-blur-sm text-background-50 text-xs px-2 py-1 rounded-md">
            {readTime} 分钟阅读
          </div>
        </div>
      </Link>
      <div className="p-4 md:p-5">
        <Link
          to={`/category/${categorySlug}`}
          className="inline-block text-xs font-medium text-primary-500 bg-primary-50 px-2 py-0.5 rounded-full mb-2 hover:bg-primary-100 transition-colors"
        >
          {categoryName}
        </Link>
        <Link to={`/article/${slug}`} className="block group/link">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 group-hover/link:text-primary-500 transition-colors duration-200 line-clamp-2 mb-2">
            {title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-user-line w-3 h-3 flex items-center justify-center"></i>
              {author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line w-3 h-3 flex items-center justify-center"></i>
              {publishedAt}
            </span>
          </div>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
            {views >= 10000 ? `${(views / 10000).toFixed(1)}万` : views}
          </span>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-background-200/70">
            {tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}